import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Spinner, Input, Badge, Label } from 'reactstrap';
import { Icon } from '@iconify/react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const ReverseBankLinkModal = ({ isOpen, toggle, selectedExpense, onSuccess }) => {
    const [movements, setMovements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMovementId, setSelectedMovementId] = useState(null);
    const [linking, setLinking] = useState(false);
    const [generateSiigoRp, setGenerateSiigoRp] = useState(false);

    // Siigo Intelligence
    const liveSiigoBalance = selectedExpense?.live_siigo_balance;
    const isLiveBalanceAvailable = liveSiigoBalance !== undefined && liveSiigoBalance !== null;
    const isCreditNoteApplied = isLiveBalanceAvailable && selectedExpense.amount && liveSiigoBalance < selectedExpense.amount;

    useEffect(() => {
        if (isOpen && selectedExpense) {
            fetchOrphanMovements();
            setGenerateSiigoRp(false);
        } else {
            setMovements([]);
            setSearchTerm('');
            setSelectedMovementId(null);
        }
    }, [isOpen, selectedExpense]);

    const fetchOrphanMovements = async () => {
        try {
            setLoading(true);
            const response = await api.get('/wallet/bancolombia/orphan-movements');
            if (response.data.success) {
                setMovements(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching orphan movements:', error);
            toast.error('Error al obtener movimientos de Bancolombia');
        } finally {
            setLoading(false);
        }
    };

    const handleLink = async () => {
        if (!selectedMovementId) return;

        try {
            setLinking(true);
            const response = await api.put(`/wallet/movements/${selectedMovementId}/link-expense`, {
                expense_id: selectedExpense.id
            });

            if (generateSiigoRp) {
                toast.loading('Generando RP en Siigo...', { id: 'rp-toast' });
                const rpRes = await api.post('/expenses/generate-siigo-rp', { expense_id: selectedExpense.id });
                toast.dismiss('rp-toast');
                if (!rpRes.data.success) {
                    toast.error(`Aviso: Se vinculó el banco, pero falló Siigo: ${rpRes.data.message}`);
                } else {
                    toast.success(`Movimiento vinculado y RP ${rpRes.data.rp_number} generado en Siigo.`);
                }
            } else {
                if (response.data.success) toast.success('Movimiento vinculado exitosamente.');
            }

            if (onSuccess) onSuccess();
            toggle();
            
        } catch (error) {
            console.error('Error linking:', error);
            toast.error(error.response?.data?.message || 'Error al vincular con el banco');
        } finally {
            setLinking(false);
        }
    };

    const currencyFormatter = new Intl.NumberFormat('es-CO', {
        style: 'currency', currency: 'COP', minimumFractionDigits: 0
    });

    const filteredMovements = movements.filter(m => 
        (m.descripcion || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(m.valor).includes(searchTerm) ||
        (m.fecha || '').includes(searchTerm)
    );

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle} className="border-bottom pb-3">
                <div className="d-flex align-items-center">
                    <div className="icon icon-shape bg-info text-white rounded-circle mr-3">
                        <Icon icon="mdi:bank-transfer" style={{fontSize: '24px'}} />
                    </div>
                    <div>
                        <h4 className="mb-0">Vincular Salida de Banco Directamente</h4>
                        <span className="text-muted text-sm px-0">
                            Gasto actual: <strong>{selectedExpense?.provider_name} ({currencyFormatter.format(selectedExpense?.amount || 0)})</strong>
                        </span>
                    </div>
                </div>
            </ModalHeader>
            <ModalBody className="bg-secondary" style={{minHeight: '300px'}}>
                <div className="mb-3">
                    <Input 
                        type="text" 
                        placeholder="Buscar por descripción, valor o fecha..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="form-control-alternative"
                    />
                </div>

                {loading ? (
                    <div className="text-center py-5"><Spinner color="info" /></div>
                ) : filteredMovements.length === 0 ? (
                    <div className="text-center py-5 bg-white rounded shadow-sm">
                        <Icon icon="mdi:bank-off" className="text-muted mb-2" style={{fontSize: '40px'}}/>
                        <p className="text-muted mb-0">No se encontraron transferencias huérfanas o pendientes.</p>
                    </div>
                ) : (
                    <div className="bg-white rounded shadow-sm overflow-hidden" style={{maxHeight: '400px', overflowY: 'auto'}}>
                        <Table responsive className="align-items-center table-flush table-hover m-0">
                            <thead className="thead-light" style={{position: 'sticky', top: 0, zIndex: 1}}>
                                <tr>
                                    <th style={{width: '40px'}}>Sel.</th>
                                    <th>Fecha</th>
                                    <th>Descripción Bancolombia</th>
                                    <th className="text-right">Valor Extraído</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMovements.map(m => (
                                    <tr 
                                        key={m.id} 
                                        onClick={() => setSelectedMovementId(m.id)}
                                        style={{cursor: 'pointer', backgroundColor: selectedMovementId === m.id ? '#eef2ff' : 'transparent'}}
                                    >
                                        <td>
                                            <div className="custom-control custom-radio">
                                                <input 
                                                    type="radio" 
                                                    className="custom-control-input" 
                                                    checked={selectedMovementId === m.id}
                                                    readOnly
                                                />
                                                <label className="custom-control-label"></label>
                                            </div>
                                        </td>
                                        <td>{new Date(m.fecha).toLocaleDateString()}</td>
                                        <td style={{whiteSpace: 'normal', maxWidth: '300px', fontSize: '13px'}}>{m.descripcion}</td>
                                        <td className="text-right font-weight-bold text-danger">
                                            {currencyFormatter.format(Math.abs(m.valor))}
                                            {Math.abs(m.valor) === parseFloat(selectedExpense?.amount) && (
                                                <Badge color="success" className="ml-2">¡Monto Exacto!</Badge>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* SIIGO INTELLIGENCE & AUTO-RP PARAMS */}
                {selectedExpense && (
                    <div className="mt-4 border-top pt-3">
                        {isLiveBalanceAvailable && (
                            <div className={`p-3 mb-3 rounded border ${isCreditNoteApplied ? 'bg-warning-light border-warning' : 'bg-light border-light'}`} style={isCreditNoteApplied ? {backgroundColor: '#fffcf5'} : {}}>
                                <div className="d-flex align-items-center">
                                    <Icon icon={isCreditNoteApplied ? "mdi:bell-ring" : "mdi:check-circle"} className={`mr-2 ${isCreditNoteApplied ? 'text-warning' : 'text-success'}`} style={{fontSize: '1.5rem'}} />
                                    <div>
                                        <h5 className="mb-0">
                                            Inteligencia Siigo: Saldo Actual <strong>{currencyFormatter.format(liveSiigoBalance)}</strong>
                                        </h5>
                                        {isCreditNoteApplied ? (
                                            <p className="text-sm mb-0 text-muted mt-1">
                                                <strong>¡Atención!</strong> El saldo en Siigo es MENOR al total de la factura. Asegúrate de verificar si existen Notas Crédito antes de generar un RP por el total original.
                                            </p>
                                        ) : (
                                            <p className="text-sm mb-0 text-muted mt-1">
                                                El saldo en Siigo coincide con el total. No se detectan Notas Crédito.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="custom-control custom-checkbox bg-white p-3 rounded shadow-sm border">
                            <Input 
                                type="checkbox" 
                                className="custom-control-input" 
                                id="generate_siigo_rp_bank" 
                                checked={generateSiigoRp}
                                onChange={(e) => setGenerateSiigoRp(e.target.checked)}
                            />
                            <Label className="custom-control-label font-weight-bold text-primary ml-2" htmlFor="generate_siigo_rp_bank" style={{cursor: 'pointer'}}>
                                ⚡ Generar Comprobante de Egreso (RP) en Siigo
                            </Label>
                            <p className="text-muted text-xs ml-4 mt-1 mb-0">
                                Emitirá un RP pagado con Bancolombia asociado a la factura {selectedExpense?.siigo_fc_number}
                            </p>
                        </div>
                    </div>
                )}

            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>Cancelar</Button>
                <Button color="info" onClick={handleLink} disabled={!selectedMovementId || linking}>
                    {linking ? <Spinner size="sm" /> : <><Icon icon="mdi:link-variant" className="mr-1"/> Ejecutar Conciliación y Auto-RP</>}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ReverseBankLinkModal;
